import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { amountOptions, Funds, Industries, Occupations } from "../constants";
import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import {
  Box,
  Grid,
  FormControl,
  Select,
  Autocomplete,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  alpha,
  Divider,
} from "@mui/material";
import {
  allowOnlyAlphabetsCityState,
  OnlyAllowedInputBankPartner,
  OnlyAllowedInputZipCode,
} from ".";
import { useSelector } from "react-redux";
import ModalContent from "../../Common/Modal";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";

const IdentityVerificationEmployee = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
}) => {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);
  const theme = localStorage.getItem("theme");
  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");
  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol");
  const [PEPOpen, setPEPOpen] = useState(false);
  const handleClosePEP = () => {
    setPEPOpen(false);
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      industry: userDetails?.industry || "",
      occupation: userDetails?.occupation || "",
      sourceOfFunds: userDetails?.sourcesOfFunds || "",
      monthlyTransactionVolume: userDetails?.txnVolumeId || "",
      annualIncome: userDetails?.annualIncomeId || "",
      netWorth: userDetails?.netWorthId || "",
      employmentCategory: userDetails?.empCategory || "",
      employmentType: userDetails?.empType || "",
      pepStatus: userDetails?.pep || "",
      bankingPartner: userDetails?.bankingPartner || "",
      bankingDuration: userDetails?.relationWithBank || "",
      purposeAccount:
        userDetails?.accountPurpose?.split("-")?.[0]?.trim() ?? "",
      others:
        userDetails?.accountPurpose?.split("-")?.slice(1).join("-").trim() ??
        "",
      thirdParty: userDetails?.thirdParty || "",
    },
  });

  useEffect(() => {
    if (userDetails) {
      reset({
        industry: userDetails?.industry || "",
        occupation: userDetails?.occupation || "",
        sourceOfFunds: userDetails?.sourcesOfFunds || "",
        monthlyTransactionVolume: userDetails?.txnVolumeId || "",
        annualIncome: userDetails?.annualIncomeId || "",
        netWorth: userDetails?.netWorthId || "",
        employmentCategory: userDetails?.empCategory || "",
        employmentType: userDetails?.empType || "",
        pepStatus: String(userDetails?.pep) || "",
        bankingPartner: userDetails?.bankingPartner || "",
        bankingDuration: userDetails?.relationWithBank || "",
        purposeAccount:
          userDetails?.accountPurpose?.split("-")?.[0]?.trim() ?? "",
        others:
          userDetails?.accountPurpose?.split("-")?.slice(1).join("-").trim() ??
          "",
        thirdParty: String(userDetails?.thirdParty) || "",
      });
    }
  }, [userDetails, reset]);

  const onSubmit = async (data) => {
    const payload = {
      industry: data?.industry,
      occupation: data?.occupation,
      sourcesOfFunds: data?.sourceOfFunds,
      txnVolumeId: data?.monthlyTransactionVolume,
      annualIncomeId: data?.annualIncome,
      netWorthId: data?.netWorth,
      empCategory: data?.employmentCategory,
      empType: data?.employmentType,
      pep: data?.pepStatus,
      bankingPartner: data?.bankingPartner,
      relationWithBank: data?.bankingDuration,
      accountPurpose:
        data?.purposeAccount === "Others"
          ? `Others-${data?.others}`
          : data?.purposeAccount,
      thirdParty: data?.thirdParty,
      uuid: uuid,
    };
    setIsloading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/saveUserKycData`,
        payload,
        { headers: { Authorization: `BEARER ${token}` } }
      );
      if (response?.data?.error?.error_data === 0) {
        setIsloading(false);
        showToast("success", response?.data?.error?.error_msg);
        handleSteps();
      } else {
        setIsloading(false);
        showToast("error", response?.data?.error?.error_msg);
      }
    } catch (error) {
      setIsloading(false);
      showToast("error", error?.response?.data?.error?.error_msg);
    }
  };

  const amountData = [
    { value: 1, label: `${homeCurrencySymbol}0 - 10,000` },
    {
      value: 2,
      label: `${homeCurrencySymbol}10,000 - ${homeCurrencySymbol}25,000`,
    },
    {
      value: 3,
      label: `${homeCurrencySymbol}25,000 - ${homeCurrencySymbol}50,000`,
    },
    {
      value: 4,
      label: `${homeCurrencySymbol}50,000 - ${homeCurrencySymbol}100,000`,
    },
    {
      value: 5,
      label: `${homeCurrencySymbol}100,000 - ${homeCurrencySymbol}200,000`,
    },
    {
      value: 6,
      label: `${homeCurrencySymbol}200,000 - ${homeCurrencySymbol}300,000`,
    },
    {
      value: 8,
      label: `${homeCurrencySymbol}300,000 - ${homeCurrencySymbol}1,000,000`,
    },
    { value: 7, label: `${homeCurrencySymbol}1,000,000+` },
  ];

  return (
    <Box sx={card}>
      {/* Step Header */}
      <Box sx={stepHeader}>
        <Box
          sx={{
            ...stepBadge,
            backgroundColor: alpha(btncolor || "#1976d2", 0.12),
            color: btncolor,
          }}
        >
          <WorkOutlineOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={stepTitle}>Employment & Financial Details</Typography>
          <Typography sx={stepCaption}>
            Provide your employment background and financial information
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2.5}>
          {/* Industry */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Industry *</Typography>
            <Controller
              name="industry"
              control={control}
              defaultValue=""
              rules={{ required: "Industry is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={Industries}
                  getOptionLabel={(option) => option || ""}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.industry}
                      helperText={errors.industry?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Occupation */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Occupation *</Typography>
            <Controller
              name="occupation"
              control={control}
              rules={{ required: "Occupation is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={Occupations}
                  getOptionLabel={(option) => option || ""}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.occupation}
                      helperText={errors.occupation?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Sources of funds */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Sources of Funds *</Typography>
            <Controller
              name="sourceOfFunds"
              control={control}
              rules={{ required: "Source of funds is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={Funds}
                  getOptionLabel={(option) => option || ""}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.sourceOfFunds}
                      helperText={errors.sourceOfFunds?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Monthly transaction volume */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>
              Monthly Transaction Volume *
            </Typography>
            <Controller
              name="monthlyTransactionVolume"
              control={control}
              rules={{ required: "Monthly transaction volume is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={amountData}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    amountData.find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.monthlyTransactionVolume}
                      helperText={errors.monthlyTransactionVolume?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Annual income */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Annual Income *</Typography>
            <Controller
              name="annualIncome"
              control={control}
              rules={{ required: "Annual income is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={amountData}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    amountData.find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.annualIncome}
                      helperText={errors.annualIncome?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Net worth */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Net Worth *</Typography>
            <Controller
              name="netWorth"
              control={control}
              rules={{ required: "Net worth is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={amountData}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    amountData.find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.netWorth}
                      helperText={errors.netWorth?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Employment category */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Employment Category *</Typography>
            <Controller
              name="employmentCategory"
              control={control}
              rules={{ required: "Employment category is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={["Contractor", "Employee", "Freelancer"]}
                  getOptionLabel={(option) => option || ""}
                  onChange={(_, data) => field.onChange(data)}
                  value={field.value || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.employmentCategory}
                      helperText={errors.employmentCategory?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Employment type */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Employment Type *</Typography>
            <Controller
              name="employmentType"
              control={control}
              rules={{ required: "Employment type is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={["Full time", "Part time"]}
                  getOptionLabel={(option) => option || ""}
                  onChange={(_, data) => field.onChange(data)}
                  value={field.value || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.employmentType}
                      helperText={errors.employmentType?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* PEP Status */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>
              Politically Exposed Person (PEP)? *{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setPEPOpen(true);
                }}
                style={{
                  color: btncolor,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              >
                Who is a PEP?
              </a>
            </Typography>
            <Controller
              name="pepStatus"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={[
                    { value: "1", label: "Yes" },
                    { value: "0", label: "No" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    [
                      { value: "1", label: "Yes" },
                      { value: "0", label: "No" },
                    ].find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.pepStatus}
                      helperText={errors.pepStatus?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Purpose of account */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Purpose of the Account *</Typography>
            <Controller
              name="purposeAccount"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={[
                    "Day Trading",
                    "Investing",
                    "Managing User Assets",
                    "Lowering taxes",
                    "Cross-border payments",
                    "Domestic payments",
                    "Others",
                  ]}
                  getOptionLabel={(option) =>
                    option === "Others"
                      ? "Others (Please explain below)"
                      : option || ""
                  }
                  onChange={(_, data) => field.onChange(data)}
                  value={field.value || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.purposeAccount}
                      helperText={errors.purposeAccount?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Others field — conditional */}
          {watch("purposeAccount") === "Others" && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={fieldLabel}>Specify Purpose *</Typography>
              <TextField
                fullWidth
                multiline
                rows={1}
                {...register("others", {})}
                onKeyDown={allowOnlyAlphabetsCityState}
                sx={modernField}
              />
            </Grid>
          )}

          {/* Banking partner */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>Current Banking Partner *</Typography>
            <TextField
              fullWidth
              placeholder="Your banking partner name"
              error={!!errors.bankingPartner}
              helperText={errors.bankingPartner?.message}
              {...register("bankingPartner", {
                required: "Banking partner name is required",
              })}
              onKeyDown={OnlyAllowedInputBankPartner}
              sx={modernField}
            />
          </Grid>

          {/* Banking duration */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>
              Banking Relationship Duration *
            </Typography>
            <Controller
              name="bankingDuration"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={["0-1 year", "1-2 year", "2+ years"]}
                  getOptionLabel={(option) =>
                    option === "0-1 year"
                      ? "0–1 year"
                      : option === "1-2 year"
                      ? "1–2 years"
                      : option || ""
                  }
                  onChange={(_, data) => field.onChange(data)}
                  value={field.value || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.bankingDuration}
                      helperText={errors.bankingDuration?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>

          {/* Third party */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={fieldLabel}>
              Acting on Behalf of a Third Party? *
            </Typography>
            <Controller
              name="thirdParty"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={[
                    { value: "1", label: "Yes" },
                    { value: "0", label: "No" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    [
                      { value: "1", label: "Yes" },
                      { value: "0", label: "No" },
                    ].find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Any"
                      error={!!errors.thirdParty}
                      helperText={errors.thirdParty?.message}
                      sx={modernField}
                    />
                  )}
                  sx={selectInner}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3.5,
            pt: 2.5,
            borderTop: "1px solid rgba(125,125,125,0.12)",
            display: "flex",
            gap: 1.5,
          }}
        >
          <ContainedButton
            variant="contained"
            onClick={() => handleBack()}
            sx={{ ...submitBtn, opacity: 0.85 }}
          >
            Back
          </ContainedButton>
          <ContainedButton type="submit" variant="contained" sx={submitBtn}>
            Save and Next
          </ContainedButton>
        </Box>
      </Box>

      {/* PEP info modal — untouched */}
      <ModalContent
        open={PEPOpen}
        onClose={handleClosePEP}
        title={"Who is a PEP?"}
        width="md"
      >
        <Typography gutterBottom>
          <b>A politically exposed person (PEP)</b> including Head of
          International Organization ("HIO"), is "an individual who is or has
          been entrusted with a prominent public position. A PEP can be either
          "Foreign" or "Domestic".
        </Typography>
        <br />
        <Typography gutterBottom>
          <b>Head of International Organization (HIO)</b>
        </Typography>
        <br />
        <Typography gutterBottom>
          HIO is an individual heading an international organization that is
          established by a treaty or other type of instrument governed by
          international law and possesses its own legal personality, such as the
          United Nations, the World Health Organization, etc. HIO is the primary
          person who leads that organization, e.g., a president or CEO.
        </Typography>
        <br />
        <Typography gutterBottom>
          <b>Family members of PEP or HIO:</b>
        </Typography>
        <br />
        <Typography gutterBottom>
          Family members of PEP or HIO are recognized as PEP or HIO by virtue of
          their family relationship. PEP and HIO family members include spouses
          or common-law partners, ex-spouse or ex-common-law partner, biological
          or adoptive children, parents, the parents of spouses or common-law
          partners, and siblings.
        </Typography>
        <br />
        <Typography gutterBottom>
          <b>Close Associates of PEP or HIO:</b>
        </Typography>
        <br />
        <Typography gutterBottom>
          A close associate is a person connected to a PEP or a HIO for personal
          or business reasons. Examples of close associates include, but are not
          limited to, business partners, persons in a romantic relationship with
          a PEP or a HIO, and persons involved in financial transactions with a
          PEP or a HIO.
        </Typography>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <OutlinedButton onClick={handleClosePEP} variant="outlined">
            Cancel
          </OutlinedButton>
        </Box>
      </ModalContent>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const stepNum = {
  ml: "auto",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  px: 1.5,
  py: 0.6,
  borderRadius: "20px",
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.75,
};

const modernField = {
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  "& .MuiFormHelperText-root": { color: "#ef4444" },
};

const selectInner = {
  height: "48px",
  borderRadius: "10px",
  backgroundColor: "var(--body-color-less2)",
  color: "var(--text-color)",
  fontSize: "0.9rem",
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& fieldset": { top: 0 },
  "& legend": { display: "none" },
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default IdentityVerificationEmployee;
